import dayjs from "dayjs";
import {
    ADDRESS_REMOVAL,
    BANKRUPTCY,
    BUSINESS_VIP,
    CLUB_FICO_GUARANTEED,
    CREDIT_REPORT,
    FUNDING,
    HOMESTEAD,
    ID_THEFT,
    ID_THEFT_ALERTS,
    INQUIRIES,
    JUDGEMENT,
    LIVING_TRUST,
    MAILING_ADDRESS,
    MANUAL_PROGRAM,
    NEW_CREDIT_CARD,
    PUBLIC_RECORDS,
    RAPID_RESCORE,
    REGULAR,
    REMOVE_DISPUTES,
    RFV,
    SCORE_SIMULATOR,
    SINGLE_NEGOTIATION,
    TRADELINES,
    TRADELINES_FUNDING,
    VIP_CREDIT,
    ZERO_TO_HERO,
    BUYER,
    CORPORATIONS,
    VOE,
    WAGE_GARNISHMENTS,
    ALL_CLUBS,
    ALL_TRADELINES,
    COPY_TITLE,
    DOCUMENT_PREPARATION,
    POLICY,
} from "./fields";
import { getInitStatusREProgram } from "../../../clients/real-estate-tab/shared/utils";
import { CA_ZIPCODES_WITH_DISCOUNT } from "./constants";

export const getFieldsByProgram = (program: string) => {
    switch (program) {
        case "Credit Report":
            return CREDIT_REPORT;
        case "Tradelines":
        case "Tradelines Funding":
        case "Tradelines Borrower":
            return TRADELINES;
        case "Single Negotiation":
            return SINGLE_NEGOTIATION;
        case "Address Removal":
            return ADDRESS_REMOVAL;
        case "RFV":
            return RFV;
        case "Bankruptcy":
            return BANKRUPTCY;
        case "Judgement":
            return JUDGEMENT;
        case "Club Fico Guaranteed":
        case "CLUB 620":
        case "CLUB 620 CV":
        case "CLUB 640":
        case "CLUB 660":
        case "CLUB 680":
        case "CLUB 700":
        case "CLUB 720":
        case "CLUB 740":
        case "CLUB 760":
        case "CLUB 780":
        case "CLUB 800":
        case "CLUB 620 + Address Removal":
        case "CLUB 640 + Address Removal":
        case "CLUB 660 + Address Removal":
        case "CLUB 680 + Address Removal":
        case "CLUB 700 + Address Removal":
        case "CLUB 720 + Address Removal":
        case "CLUB 740 + Address Removal":
        case "CLUB 760 + Address Removal":
        case "CLUB 780 + Address Removal":
        case "CLUB 800 + Address Removal":
        case "CLUB 620 2nd Contract":
        case "CLUB 620 CV 2nd Contract":
        case "CLUB 640 2nd Contract":
        case "CLUB 660 2nd Contract":
        case "CLUB 680 2nd Contract":
        case "CLUB 700 2nd Contract":
        case "CLUB 720 2nd Contract":
        case "CLUB 740 2nd Contract":
        case "CLUB 760 2nd Contract":
        case "CLUB 780 2nd Contract":
        case "CLUB 800 2nd Contract":
        case "CLUB 620 VIP":
        case "CLUB 640 VIP":
        case "CLUB 660 VIP":
        case "CLUB 680 VIP":
            return CLUB_FICO_GUARANTEED;
        case "Credito y Casas":
        case "Mortgage":
        case "Seller":
        case "Refinancing":
            return MANUAL_PROGRAM;
        case "Inquiries":
            return INQUIRIES;
        case "Rapid Rescore":
            return RAPID_RESCORE;
        case "ID Theft Alert":
            return ID_THEFT_ALERTS;
        case "ID Theft":
            return ID_THEFT;
        case "Public Records":
            return PUBLIC_RECORDS;
        case "Mailing Address":
            return MAILING_ADDRESS;
        case "Score Simulator":
            return SCORE_SIMULATOR;
        case "Regular":
            return REGULAR;
        case "New Credit Card":
            return NEW_CREDIT_CARD;
        case "Remove Disputes":
            return REMOVE_DISPUTES;
        case "Homestead":
            return HOMESTEAD;
        case "Living Trust":
            return LIVING_TRUST;
        case "VIP Credit":
            return VIP_CREDIT;
        case "Business VIP":
            return BUSINESS_VIP;
        case "Funding":
        case "Funding - Business":
        case "Funding - Equity":
        case "Funding - Personal":
            return FUNDING;
        case "Zero to Hero":
            return ZERO_TO_HERO;
        case "Buyer":
            return BUYER;
        case "Corporations":
            return CORPORATIONS;
        case "VOE":
            return VOE;
        case "Wage Garnishments":
            return WAGE_GARNISHMENTS;
        case "Copy Title":
            return COPY_TITLE;
        case "Document Preparation":
            return DOCUMENT_PREPARATION;
        case "Policy":
            return POLICY;
        default:
            return MANUAL_PROGRAM;
    }
};

const POSSIBLE_PROGRAMS = [
    "Club Fico Guaranteed",
    "CLUB 620",
    "CLUB 620 CV",
    "CLUB 640",
    "CLUB 660",
    "CLUB 680",
    "CLUB 700",
    "CLUB 720",
    "CLUB 740",
    "CLUB 760",
    "CLUB 780",
    "CLUB 800",
    "CLUB 620 + Address Removal",
    "CLUB 640 + Address Removal",
    "CLUB 660 + Address Removal",
    "CLUB 680 + Address Removal",
    "CLUB 700 + Address Removal",
    "CLUB 720 + Address Removal",
    "CLUB 740 + Address Removal",
    "CLUB 760 + Address Removal",
    "CLUB 780 + Address Removal",
    "CLUB 800 + Address Removal",
    "CLUB 620 2nd Contract",
    "CLUB 620 CV 2nd Contract",
    "CLUB 640 2nd Contract",
    "CLUB 660 2nd Contract",
    "CLUB 680 2nd Contract",
    "CLUB 700 2nd Contract",
    "CLUB 720 2nd Contract",
    "CLUB 740 2nd Contract",
    "CLUB 760 2nd Contract",
    "CLUB 780 2nd Contract",
    "CLUB 800 2nd Contract",
    "CLUB 620 VIP",
    "CLUB 640 VIP",
    "CLUB 660 VIP",
    "CLUB 680 VIP",
];

export const isClubFico = (program: string) => {
    return POSSIBLE_PROGRAMS.includes(program);
};

export const needsValidation = (program: string, special_pricing?: boolean, club_type?: string) => {
    const programsWithValidation = [
        "Tradelines",
        "Single Negotiation",
        "ID Theft",
        "Club Fico Guaranteed",
        ...ALL_CLUBS,
        ...ALL_TRADELINES,
    ];
    const programsWithoutValidation = [
        "Credit Report",
        "Address Removal",
        "RFV",
        "Bankruptcy",
        "Judgement",
        "Credito y Casas",
        "Refinancing",
        "Mortgage",
        "Buyer",
        "Seller",
        "Inquiries",
        "Rapid Rescore",
        "ID Theft Alert",
        "Public Records",
        "Mailing Address",
        "Score Simulator",
        "Regular",
        "New Credit Card",
        "Remove Disputes",
        "Homestead",
        "Living Trust",
        "VIP Credit",
        "Copy Title",
        "Document Preparation",
        "Policy",
    ];

    if (special_pricing) return true;

    if (programsWithValidation.includes(program)) return true;
    else if (programsWithoutValidation.includes(program)) return false;
};

interface IAdditionalInfo {
    accounts_positive?: number;
    accounts_negative?: number;
    tradelines?: number;
    bk_type: string;
    cost?: string;
    price?: string;
    inquiries?: number;
    simulations?: number;
    records?: number;
    ex?: boolean;
    eq?: boolean;
    tu?: boolean;
    state?: string;
    creditor_1?: string;
    creditor_2?: string;
    creditor_3?: string;
    collection_amount_1?: number;
    collection_amount_2?: number;
    collection_amount_3?: number;
    account_1?: string;
    account_2?: string;
    account_3?: string;
    club?: string;
    club_type?: string;
    num_copies?: number;
    policy_type?: string;
    living_trust_type?: string;
    number_of_payments?: number;
    amount_approved?: string;
    property_value?: number;
    type?: string;
    payment_interval?: number;
    next_payment?: string;
    zip?: string;
    tradelines_type?: string;
    repositions?: number;
    num_collections?: number;
    num_disputes?: number;
    num_inquiries?: number;
    num_public_records?: number;
    num_open_accounts?: number;
    mix_social_itin?: boolean;
}

const getSingleNegotiationCollectionCost = (amount: number) => {
    if (amount <= 1000) return 150;
    else if (amount <= 2000) return 250;
    else if (amount <= 3000) return 350;
    else return 450;
};

interface ProgramPrice {
    cost: number;
    price: number | null;
}

const PROGRAMS_WITH_CR_COST = [
    "Inquiries",
    "Zero to Hero",
    "Single Negotiation",
    "Remove Disputes",
    "Regular",
    "Business VIP",
    "Club Fico Guaranteed",
    "Judgement",
    "Living Trust",
    "New Credit Card",
    ...ALL_CLUBS,
]

export const calculateProgramPrice = (
    program: string,
    cr_pricing: number,
    number_of_payments?: number,
    additional_info?: IAdditionalInfo
) => {
    // For Credito y Casas program will be
    if (program === "Credito y Casas" ||
        program === "Refinancing" ||
        program === "Mortgage" ||
        program === "Buyer" ||
        program === "Seller" ||
        program === "Taxes" ||
        program === "Book Keeping" ||
        program === "Policy"
    )
        return { cost: additional_info?.cost, price: additional_info?.price };
    if (program.includes("Funding - ") || program === 'Funding') return { cost: null, price: null };

    let { cost, price } = getProgramPrice(program, cr_pricing, additional_info);
    if (PROGRAMS_WITH_CR_COST.includes(program)) {
        cost += cr_pricing;
    }

    return { cost: cost, price: price };
};

const isHighPriceArea = ({ state, zip }: { state?: string; zip?: string }) => {
    return !(state !== "CA" || CA_ZIPCODES_WITH_DISCOUNT.includes(zip || ""))
}

const getProgramPrice = (
    program: string,
    cr_pricing: number,
    additional_info?: IAdditionalInfo
): ProgramPrice => {
    let cost = 0;
    switch (program) {
        case "Credit Report":
            return { cost: cr_pricing, price: cr_pricing };
        case "Tradelines":
        case "Tradelines Borrower":
        case "Tradelines Funding":
            let tradelineCost = 600;
            if (additional_info?.tradelines_type === "Funding" || additional_info?.tradelines_type === "Borrower" || program === "Tradelines Funding" || program === "Tradelines Borrower") {
                tradelineCost = 1000;
            }
            cost = tradelineCost * (additional_info?.tradelines || 0);
            return { cost, price: null };
        case "Single Negotiation":
            // if (
            //     additional_info?.creditor_1 &&
            //     additional_info.collection_amount_1 &&
            //     additional_info.account_1
            // )
            //     cost += getSingleNegotiationCollectionCost(additional_info.collection_amount_1);
            // if (
            //     additional_info?.creditor_2 &&
            //     additional_info.collection_amount_2 &&
            //     additional_info.account_2
            // )
            //     cost += getSingleNegotiationCollectionCost(additional_info.collection_amount_2);
            // if (
            //     additional_info?.creditor_3 &&
            //     additional_info.collection_amount_3 &&
            //     additional_info.account_3
            // )
            //     cost += getSingleNegotiationCollectionCost(additional_info.collection_amount_3);
            cost = 0;
            if (additional_info?.creditor_1 && additional_info.collection_amount_1 && additional_info.account_1) cost += 500;
            if (additional_info?.creditor_2 && additional_info.collection_amount_2 && additional_info.account_2) cost += 500;
            if (additional_info?.creditor_3 && additional_info.collection_amount_3 && additional_info.account_3) cost += 500;
            return { cost, price: null };
        case "Address Removal":
            return { cost: 500, price: null };
        case "RFV":
            return { cost: 500, price: null };
        case "Bankruptcy":
            cost = additional_info?.bk_type === "SINGLE BK" ? 2378 : 3078;
            return { cost, price: null };
        case "Judgement":
            return { cost: 1100, price: null };
        case "Club Fico Guaranteed":
        case "CLUB 620":
        case "CLUB 620 CV":
        case "CLUB 640":
        case "CLUB 660":
        case "CLUB 680":
        case "CLUB 700":
        case "CLUB 720":
        case "CLUB 740":
        case "CLUB 760":
        case "CLUB 780":
        case "CLUB 800":
        case "CLUB 620 + Address Removal":
        case "CLUB 640 + Address Removal":
        case "CLUB 660 + Address Removal":
        case "CLUB 680 + Address Removal":
        case "CLUB 700 + Address Removal":
        case "CLUB 720 + Address Removal":
        case "CLUB 740 + Address Removal":
        case "CLUB 760 + Address Removal":
        case "CLUB 780 + Address Removal":
        case "CLUB 800 + Address Removal":
        case "CLUB 620 2nd Contract":
        case "CLUB 620 CV 2nd Contract":
        case "CLUB 640 2nd Contract":
        case "CLUB 660 2nd Contract":
        case "CLUB 680 2nd Contract":
        case "CLUB 700 2nd Contract":
        case "CLUB 720 2nd Contract":
        case "CLUB 740 2nd Contract":
        case "CLUB 760 2nd Contract":
        case "CLUB 780 2nd Contract":
        case "CLUB 800 2nd Contract":
        case "CLUB 620 VIP":
        case "CLUB 640 VIP":
        case "CLUB 660 VIP":
        case "CLUB 680 VIP":
            if (additional_info?.repositions && additional_info?.repositions > 0) {
                cost = 2500;
                if (!isHighPriceArea({ ...additional_info })) {
                    cost = 1800;
                }
            } else if (additional_info?.club_type === "Regular" || additional_info?.club_type === "Regular + Address Removal") {
                const n = additional_info?.accounts_negative || 0;
                const p = additional_info?.accounts_positive || 0;
                cost = 0;
                if (n <= 4) {
                    cost = 600;
                    if (2 - p > 0) cost += (2 - p) * 600;
                } else if (n <= 8) {
                    //todo: comenta
                    const positives = Math.min(additional_info?.accounts_positive || 0, 2);
                    /* cost = 1200;
                    if (2 - p > 0) cost += (2 - p) * 600;
                    if (p == 0 && cost === 2400) cost = 2500; */
                    cost = 2500 - positives * 700;
                } else if (n >= 9) {
                    cost = 2500;
                }
                // If in CA, apply discount if zip code is in the list
                // If any other state, always apply discount
                if (!isHighPriceArea({ ...additional_info })) {
                    cost = cost > 1800 ? 1800 : cost;
                }
            } else if (additional_info?.club_type === "2nd Contract") {
                cost = 600;
            }
            if (additional_info?.club_type === "Regular + Address Removal") {
                cost += 500;
            }
            // if (additional_info?.num_disputes && additional_info?.num_disputes > 0) {
            //     cost += Math.min(additional_info.num_disputes * 150, 600);
            // }
            return { cost, price: null };
        case "Inquiries":
            cost = 1000; // 50 dollars will be added for credit report to make it $1000
            return { cost, price: null };
        case "Rapid Rescore":
            cost += additional_info?.eq ? 200 : 0;
            cost += additional_info?.ex ? 200 : 0;
            cost += additional_info?.tu ? 200 : 0;
            return { cost, price: null };
        case "ID Theft Alert":
            cost += additional_info?.eq ? 50 : 0;
            cost += additional_info?.ex ? 50 : 0;
            cost += additional_info?.tu ? 50 : 0;
            return { cost, price: null };
        case "ID Theft":
            cost = 600;
            const accounts = +(additional_info?.accounts_negative || 0);
            const additional_accounts = accounts > 6 ? accounts - 6 : 0;
            cost += additional_accounts * 100;
            return { cost, price: null };
        case "Public Records":
            cost = (additional_info?.records || 0) * 800;
            return { cost, price: null };
        case "Mailing Address":
            return { cost: 1000, price: null };
        case "Score Simulator":
            cost = (additional_info?.simulations || 0) * 100;
            return { cost, price: null };
        case "Regular":
            cost = 1600;
            return { cost, price: null };
        case "New Credit Card":
            return { cost: 300, price: null };
        case "Remove Disputes":
            return { cost: 400, price: 400 };
        case "Homestead":
            cost =
                additional_info?.property_value && additional_info?.property_value > 600000
                    ? 650
                    : 450;
            return { cost, price: null };
        case "Living Trust":
            return additional_info?.living_trust_type === "Joint"
                ? { cost: 2600, price: null }
                : { cost: 2200, price: null };
        case "VIP Credit":
            const n = +(additional_info?.accounts_negative || 0);
            if (n <= 5) {
                cost = 5000;
            } else if (n <= 10) {
                cost = 7500;
            } else if (n <= 15) {
                cost = 10000;
            } else {
                cost = 15000;
            }
            return { cost, price: null };
        case "Business VIP":
            return { cost: 2500, price: null };
        case "Funding":
            return { cost: 0, price: null };
        case "Zero to Hero":
            if (isHighPriceArea({ ...additional_info }))
                return { cost: 2500, price: null };
            return { cost: 1800, price: null };
        // return { cost: 2500, price: null };
        case "Corporations":
            return { cost: 1400, price: null };
        case "VOE":
            return { cost: 3000, price: null };
        case "Wage Garnishments":
            return { cost: 500, price: null };
        case "Copy Title":
            return { cost: (additional_info?.num_copies || 1) * 150, price: null };
        case "Document Preparation":
            return { cost: 450, price: null };
        case "Policy":
            // Real premium is unknown at creation ("Determined later" per
            // CLRDC-2211) — cost/price start at 0 and the actual amount is
            // set afterward via Target Premium on the Commissions tab.
            return { cost: 0, price: null };
    }
    return { price: null, cost: 0 };
};

const MAX_COMISSION_PROGRAM: any = {
    "Inquiries": 300,
    "Bankruptcy": 200,
    "Zero to Hero": 300,
    // "Tardelines": 150,
    "Single Negotiation": 150,
    "RFV": 150,
    "Address Removal": 150,
    "Rapid Rescore": 150,
    "Remove Disputes": 150,
    "Regular": 150,
    // "Tradelines Funding": 150,
    "Business VIP": 150,
    "Buy (Real Estate)": 200,
    "Sell (Real Estate)": 150,
    "Refinance (Real Estate)": 200,
    "Mortgage (Real Estate)": 200,
    // "Equity (Funding) - (HELOC & HELOAN)": 150,
    // "Business (Funding)": 200,
    // "Personal (Funding) - (Credit Cards Only)": 150,
    "Taxes": 0,
    "Corporations": 100,
    "Book Keeping": 0,
    // "Tradelines - Type Borrower": 150,
    "Mailing Address": 0,
    "VOE": 150,
    "Wage Garnishments": 150,
    "Judgement": 300,
    "New Credit Card": 300,
}

export const getProgramMaxPrice = (
    program: string,
    cr_pricing: number,
    additional_info?: IAdditionalInfo
): number | null => {
    if (MAX_COMISSION_PROGRAM[program] !== undefined) return Number(additional_info?.cost || 0) + MAX_COMISSION_PROGRAM[program];
    switch (program) {
        case "Regular":
            return (additional_info?.number_of_payments === 0 ? 1500 : 1800) + cr_pricing;
        case "Homestead":
            return (
                (additional_info?.property_value && additional_info?.property_value > 600000
                    ? 800
                    : 600) + cr_pricing
            );
        case "Living Trust":
            return additional_info?.living_trust_type === "Joint" ? 2600 : 2200;
        case "Policy":
            return 9999;
        case "Copy Title":
            return (additional_info?.num_copies || 1) * 150;
        case "Document Preparation":
            return 450;
        case "Club Fico Guaranteed":
            // 200$ Max Upsell for 2nd Contract
            if (additional_info?.club_type === "2nd Contract") return (Number(additional_info?.cost) || 0) + 200;
            const appliesDiscount = additional_info?.state !== "CA" || CA_ZIPCODES_WITH_DISCOUNT.includes(additional_info?.zip || "")
            const isAddressRemoval = additional_info?.club_type === "Regular + Address Removal";
            // If in CA, apply discount if zip code is in the list
            // If any other state, always apply discount
            // If address removal is present, add 150$ to each upsell
            if (appliesDiscount) {
                if (isAddressRemoval) return (Number(additional_info?.cost) || 0) + 350;
                return (Number(additional_info?.cost) || 0) + 200;
            }
            if (isAddressRemoval) return (Number(additional_info?.cost) || 0) + 450;
            return (Number(additional_info?.cost) || 0) + 300;
        case "Tradelines":
            return Number(additional_info?.cost) + 150;
        default:
            return null;
    }
};

export const getAgentForProgram = (program: string, agent: string, real_estate_agent?: string, funding_agent?: string, loan_officer?: string) => {
    switch (program) {
        case 'Credito y Casas':
        case 'Buyer':
        case 'Seller':
            return real_estate_agent || agent;
        case 'Refinancing':
        case 'Mortgage':
            return loan_officer || agent;
        case 'Funding':
        case 'Funding - Business':
        case 'Funding - Personal':
        case 'Funding - Equity':
            return funding_agent || agent;
        default:
            return agent;
    }
}

const getProgramName = (
    program: string,
    change_club?: boolean,
    club?: string,
    club_type?: string,
    funding_type?: string,
    tradelines_type?: string,
) => {
    if (!change_club) return program;
    if (program === "Tradelines" && tradelines_type !== "Regular") return `Tradelines ${tradelines_type}`
    if (program === 'Funding') return "Funding - " + funding_type;
    if (program !== "Club Fico Guaranteed") return program;
    if (club_type === "Regular") return club;
    if (club_type === "Regular + Address Removal") return club + " + Address Removal";
    return club + " " + club_type;
};

var utc = require("dayjs/plugin/utc");
var timezone = require("dayjs/plugin/timezone"); // dependent on utc plugin

export const newProgram = (
    id: string,
    agent_id: string,
    program: any,
    change_club?: boolean,
    lead?: boolean,
    validation?: boolean,
) => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const isReProgram = ['Buyer', 'Seller', 'Mortgage', 'Refinancing'].includes(program.name);
    return {
        // client_id: id,
        ...(lead ? { lead_id: id } : { client_id: id }),
        id: program.id || crypto.randomUUID(),
        name: getProgramName(program.name, change_club, program.club, program.club_type, program.funding_type, program.tradelines_type),
        goal: program.goal,
        contract_open: (dayjs as any)().tz("America/Los_Angeles").format("YYYY-MM-DD"),
        contact_close: null,
        payment_type: program.payment_type,
        cost: program.num_payments === 0 && program.apply_discount && !validation ? program.cost * 0.9 : program.cost,
        price: program.num_payments === 0 && program.apply_discount && !validation ? program.price * 0.9 : program.price,
        agent_id: agent_id,
        seller_id: program.seller_id,
        telemarketer_id_on_creation: program.telemarketer_id_on_creation,
        created_by: program.created_by,
        status: initProgramStatus(isReProgram, program.quote, program.name),
        accounts_positive: program.accounts_positive === "null" ? null : program.accounts_positive,
        accounts_negative: program.accounts_negative === "null" ? null : program.accounts_negative,
        validated_by: program.validated_by,
        special_pricing: program.special_pricing,
        quote: program.quote,
        external_payment: program.external_payment === true,
        info: getInfoForProgram({ ...program.info, ...program }),
    };
};

const initProgramStatus = (isReProgram: boolean, isQuote: boolean, program_name: string) => {
    if (isQuote) return "PENDING";
    if (isReProgram) return getInitStatusREProgram(program_name as any);
    return "ACTIVE";
}

export const formatProgramValidation = (info: any, data: any) => {
    return {
        agent_id: info.agent_id,
        created_at: info.created_at,
        id: info.id,
        quote_id: info.quote_id,
        status: info.status,
        info: {
            client_name: info.info.client_name,
            lead_name: info.info.lead_name,
            id: info.info.id,
            quote_id: info.info.quote_id,
            program: {
                ...data,
                info: getInfoForProgram({ ...data }),
            },
        },
    };
};

// Keeps an explicit 0 (e.g. set as a number by the credit report AI); only empty values become undefined.
const toOptionalCount = <T,>(value: T | "" | null | undefined): T | undefined =>
    value === "" || value === null || value === undefined ? undefined : (value as T);

export const getInfoForProgram = (info: IAdditionalInfo) => {
    return {
        tradelines: info.tradelines || undefined,
        bk_type: info.bk_type || undefined,
        inquiries: info.inquiries || undefined,
        simulations: info.simulations || undefined,
        records: info.records || undefined,
        num_copies: info.num_copies || undefined,
        policy_type: info.policy_type || undefined,
        living_trust_type: info.living_trust_type || undefined,
        ex: toOptionalCount(info.ex),
        eq: toOptionalCount(info.eq),
        tu: toOptionalCount(info.tu),
        state: info.state || undefined,
        creditor_1: info.creditor_1 || undefined,
        creditor_2: info.creditor_2 || undefined,
        creditor_3: info.creditor_3 || undefined,
        collection_amount_1: info.collection_amount_1 || undefined,
        collection_amount_2: info.collection_amount_2 || undefined,
        collection_amount_3: info.collection_amount_3 || undefined,
        account_1: info.account_1 || undefined,
        account_2: info.account_2 || undefined,
        account_3: info.account_3 || undefined,
        auth0_id: (info as any).auth0_id || undefined,
        notes_credit: (info as any).notes_credit || undefined,
        notes_validation: (info as any).notes_validation || undefined,
        credit_report: (info as any).credit_report || undefined,
        club: (info as any).club || undefined,
        special_pricing: (info as any).special_pricing || undefined,
        num_payments: (info as any).num_payments,
        estimated_deposit: (info as any).estimated_deposit === null || (info as any).estimated_deposit === undefined ? null : (info as any).estimated_deposit,
        recurrent_amount: (info as any).recurrent_amount || undefined,
        club_type: info.club ? (info as any).club_type : undefined,
        account_numbers: (info as any).account_numbers || undefined,
        property_value: (info as any).property_value || undefined,
        amount_approved: info.amount_approved || undefined,
        agent_commission: (info as any).agent_commission || undefined,
        commission_percent: (info as any).commission_percent || undefined,
        funding_type: (info as any).funding_type || undefined,
        num_collections: toOptionalCount((info as any).num_collections),
        num_disputes: toOptionalCount((info as any).num_disputes),
        num_inquiries: toOptionalCount((info as any).num_inquiries),
        num_public_records: toOptionalCount((info as any).num_public_records),
        num_open_accounts: toOptionalCount((info as any).num_open_accounts),
        type: (info as any).type || undefined,
        loan_type: (info as any).loan_type || undefined,
        payment_interval: (info as any).payment_interval || undefined,
        next_payment: (info as any).next_payment || undefined,
        payment_method_id: (info as any).payment_method_id,
        dont_charge_deposit_auto: (info as any).dont_charge_deposit_auto || undefined,
        preferred_payment_method: (info as any).preferred_payment_method || undefined,
        apply_discount: (info as any).apply_discount || undefined,
        mid_fico: (info as any).mid_fico || undefined,
        repositions: toOptionalCount((info as any).repositions),
        zip: (info as any).zip || undefined,
        tradelines_type: (info as any).tradelines_type || undefined,
        mix_social_itin: info.mix_social_itin,
    };
};

const getTimeDiff = (d1: dayjs.Dayjs, d2: dayjs.Dayjs) => {
    const minDiff = d1.diff(d2, "minutes");
    if (minDiff < 60) return d1.diff(d2, "minutes") + 1 + " min ago.";
    if (minDiff > 60 && minDiff < 1440) {
        if (d1.diff(d2, "hours") === 1) return d1.diff(d2, "hours") + " hour ago.";
        return d1.diff(d2, "hours") + " hours ago.";
    } else {
        if (d1.diff(d2, "days") === 1) return d1.diff(d2, "days") + " day ago.";
        else return d1.diff(d2, "days") + " days ago.";
    }
};

export const formatDashboardNotifications = (notifications: any[]) => {
    const out = [];
    const sortedNotifications = notifications.sort((a, b) => {
        return dayjs(b.created_at).diff(dayjs(a.created_at));
    });
    for (const n of sortedNotifications) {
        const d1 = dayjs(new Date());
        const d2 = dayjs(n.created_at);
        out.push({
            id: n.id,
            client: n.info.client_name,
            user: n.info.program.info.user_name,
            time: getTimeDiff(d1, d2),
            program: `
                ${n.info.program.name}
                ${n.info.program.info.club_type && n.info.program.info.club_type !== "Regular"
                    ? " " + n.info.program.info.club_type
                    : ""
                }
            `,
        });
    }
    return out;
};

export const checkPrice = (priceError: string, data: any, setPriceError: Function) => {
    if (
        data.name === "Credit Report" ||
        data.name === "Credito y Casas" ||
        data.name === "Mortgage" ||
        data.name === "Refinancing" ||
        data.name === "Buyer" ||
        data.name === "Seller" ||
        data.name === "Funding" ||
        data.name === "Taxes" ||
        data.name === "Book Keeping" ||
        data.name?.includes("Funding - ")
    ) {
        setPriceError("");
        return false;
    }
    if (priceError) {
        return true;
    }
    if (!data.price) {
        setPriceError("* Required");
        return true;
    }
    if (data.price < data.cost) {
        setPriceError("* Price cannot be lower than cost.");
        return true;
    }
    setPriceError("");
    return false;
};
